import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const companyId = () => Number(localStorage.getItem("companyId") || 0);
const today = () => new Date().toISOString().split("T")[0];

const val = (r, ...keys) => {
  for (const k of keys) {
    const x = r?.[k];
    if (x !== undefined && x !== null) return x;
  }
  return "";
};

const safe = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
};

const arr = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) =>
  num(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const errText = (e, fallback) => {
  const d = e?.response?.data;
  if (typeof d === "string" && d.trim()) return d;
  if (typeof d?.message === "string" && d.message.trim()) return d.message;
  if (typeof d?.title === "string" && d.title.trim()) return d.title;
  if (typeof e?.message === "string" && e.message.trim()) return e.message;
  return fallback;
};

const styles = {
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 700 },
  th: {
    fontSize: 10,
    color: "#fff",
    background: "#07111f",
    padding: "7px 8px",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
  },
  td: { fontSize: 11, padding: "6px 8px", whiteSpace: "nowrap" },
};

export default function PriceListPage() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    priceListCode: "",
    priceListName: "",
    priceCategory: "RETAIL",
    effectiveFrom: today(),
    effectiveTo: "",
    isDefault: false,
    isActive: true,
    notes: "",
  });

  const [detail, setDetail] = useState({
    itemId: "",
    minQty: 1,
    maxQty: "",
    price: "",
    discountPercent: 0,
    customerId: "",
    customerCategory: "",
    remarks: "",
  });

  const [details, setDetails] = useState([]);

  const stats = useMemo(
    () => ({
      items: details.length,
      avgPrice:
        details.length === 0
          ? 0
          : details.reduce((s, x) => s + num(x.price), 0) / details.length,
    }),
    [details],
  );

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    await Promise.allSettled([loadMasters(), loadRows(), loadNextCode()]);
    setLoading(false);
  };

  const loadMasters = async () => {
    try {
      const params = { companyId: companyId() || undefined };

      const [i, c] = await Promise.all([
        client.get("/inventory/items", { params }),
        client.get("/sales/customers", { params }),
      ]);

      setItems(arr(i.data));
      setCustomers(arr(c.data));
    } catch (e) {
      console.error(e);
      alert(errText(e, "Failed to load masters"));
    }
  };

  const loadNextCode = async () => {
    try {
      const res = await client.get("/price-lists/next-code", {
        params: { companyId: companyId() || undefined },
      });

      const code = safe(
        val(res.data, "PriceListCode", "priceListCode", "Code", "code"),
      );

      setForm((x) => ({
        ...x,
        priceListCode: code || `PL-${Date.now().toString().slice(-5)}`,
      }));
    } catch {
      setForm((x) => ({
        ...x,
        priceListCode: `PL-${Date.now().toString().slice(-5)}`,
      }));
    }
  };

  const loadRows = async () => {
    try {
      const res = await client.get("/price-lists", {
        params: { companyId: companyId() || undefined },
      });

      setRows(arr(res.data));
    } catch (e) {
      console.error(e);
      setRows([]);
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(
      (x) => String(val(x, "Id", "id")) === String(customerId),
    );

    return safe(val(customer, "CustomerName", "customerName", "Name", "name"));
  };

  const addDetail = () => {
    if (!detail.itemId) return alert("Select item");
    if (num(detail.minQty) <= 0)
      return alert("Minimum quantity must be greater than zero");
    if (detail.maxQty && num(detail.maxQty) < num(detail.minQty))
      return alert("Maximum quantity cannot be less than minimum quantity");
    if (num(detail.price) <= 0) return alert("Price required");

    const item = items.find(
      (x) => String(val(x, "Id", "id")) === String(detail.itemId),
    );

    setDetails((prev) => [
      ...prev,
      {
        itemId: Number(detail.itemId),
        itemCode: safe(val(item, "ItemCode", "itemCode", "Code", "code")),
        itemName: safe(val(item, "ItemName", "itemName", "Name", "name")),
        minQty: num(detail.minQty),
        maxQty: detail.maxQty ? num(detail.maxQty) : null,
        price: num(detail.price),
        discountPercent: num(detail.discountPercent),
        customerId: detail.customerId ? Number(detail.customerId) : null,
        customerCategory: safe(detail.customerCategory),
        remarks: safe(detail.remarks),
      },
    ]);

    setDetail({
      itemId: "",
      minQty: 1,
      maxQty: "",
      price: "",
      discountPercent: 0,
      customerId: "",
      customerCategory: "",
      remarks: "",
    });
  };

  const removeDetail = (index) => {
    setDetails((prev) => prev.filter((_, i) => i !== index));
  };

  const save = async () => {
    if (!safe(form.priceListCode).trim())
      return alert("Price list code required");
    if (!safe(form.priceListName).trim())
      return alert("Price list name required");
    if (form.effectiveTo && form.effectiveTo < form.effectiveFrom)
      return alert("Effective To date cannot be before Effective From date");
    if (details.length === 0) return alert("Add at least one pricing rule");

    try {
      setSaving(true);

      await client.post("/price-lists", {
        companyId: companyId(),
        priceListCode: safe(form.priceListCode).trim(),
        priceListName: safe(form.priceListName).trim(),
        priceCategory: form.priceCategory,
        effectiveFrom: form.effectiveFrom,
        effectiveTo: form.effectiveTo || null,
        isDefault: !!form.isDefault,
        isActive: !!form.isActive,
        notes: safe(form.notes).trim(),
        details,
      });

      alert("Price list saved successfully");

      setDetails([]);

      setForm({
        priceListCode: "",
        priceListName: "",
        priceCategory: "RETAIL",
        effectiveFrom: today(),
        effectiveTo: "",
        isDefault: false,
        isActive: true,
        notes: "",
      });

      await loadNextCode();
      await loadRows();
    } catch (e) {
      console.error(e);
      alert(errText(e, "Failed to save price list"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-2" style={{ fontSize: 11, padding: 0 }}>
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 2px 10px rgba(0,0,0,.08)",
        }}
      >
        <div
          style={{
            background:
              "linear-gradient(90deg, rgb(11, 79, 74), rgb(15, 118, 110))",
            color: "#fff",
            padding: "9px 12px",
          }}
        >
          <h6 style={{ fontSize: 13, fontWeight: 800, margin: 0 }}>
            <i className="bi bi-tags me-2"></i>
            Price List & Discount Rules
          </h6>

          <div style={{ fontSize: 10, opacity: 0.85 }}>
            Enterprise pricing engine with quantity rules and customer-wise
            pricing
          </div>
        </div>

        <div style={{ padding: 10 }}>
          <div className="row g-2 mb-2">
            <Stat title="Rules" value={stats.items} />
            <Stat title="Avg Price" value={`₹ ${money(stats.avgPrice)}`} />
            <Stat title="Category" value={form.priceCategory} />
            <Stat
              title="Status"
              value={form.isActive ? "ACTIVE" : "INACTIVE"}
            />
          </div>

          <Section title="Price List Header" />

          <div className="row g-2 mb-2">
            <Field label="Code">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.priceListCode}
                onChange={(e) =>
                  setForm({ ...form, priceListCode: e.target.value })
                }
              />
            </Field>

            <Field label="Name" col="col-md-3">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.priceListName}
                onChange={(e) =>
                  setForm({ ...form, priceListName: e.target.value })
                }
              />
            </Field>

            <Field label="Category">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.priceCategory}
                onChange={(e) =>
                  setForm({ ...form, priceCategory: e.target.value })
                }
              >
                <option value="RETAIL">RETAIL</option>
                <option value="WHOLESALE">WHOLESALE</option>
                <option value="DEALER">DEALER</option>
                <option value="DISTRIBUTOR">DISTRIBUTOR</option>
              </select>
            </Field>

            <Field label="From">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.effectiveFrom}
                onChange={(e) =>
                  setForm({ ...form, effectiveFrom: e.target.value })
                }
              />
            </Field>

            <Field label="To">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.effectiveTo}
                onChange={(e) =>
                  setForm({ ...form, effectiveTo: e.target.value })
                }
              />
            </Field>

            <Field label="Default">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.isDefault ? "YES" : "NO"}
                onChange={(e) =>
                  setForm({ ...form, isDefault: e.target.value === "YES" })
                }
              >
                <option value="NO">NO</option>
                <option value="YES">YES</option>
              </select>
            </Field>

            <Field label="Status">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.isActive ? "ACTIVE" : "INACTIVE"}
                onChange={(e) =>
                  setForm({ ...form, isActive: e.target.value === "ACTIVE" })
                }
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </select>
            </Field>

            <Field label="Notes" col="col-md-3">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Field>
          </div>

          <Section title="Pricing Rules" />

          <div
            className="row g-2 mb-2 p-2"
            style={{
              background: "#f8fafc",
              border: "1px solid #e9ecef",
              borderRadius: 10,
            }}
          >
            <Field label="Item" col="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={detail.itemId}
                onChange={(e) =>
                  setDetail({ ...detail, itemId: e.target.value })
                }
              >
                <option value="">Select Item</option>

                {items.map((item, index) => {
                  const id = val(item, "Id", "id") || index;
                  return (
                    <option key={id} value={id}>
                      {safe(val(item, "ItemCode", "itemCode", "Code", "code"))}{" "}
                      -{" "}
                      {safe(val(item, "ItemName", "itemName", "Name", "name"))}
                    </option>
                  );
                })}
              </select>
            </Field>

            <Field label="Min Qty">
              <input
                type="number"
                min="1"
                className="form-control form-control-sm"
                style={styles.input}
                value={detail.minQty}
                onChange={(e) =>
                  setDetail({ ...detail, minQty: e.target.value })
                }
              />
            </Field>

            <Field label="Max Qty">
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                style={styles.input}
                value={detail.maxQty}
                onChange={(e) =>
                  setDetail({ ...detail, maxQty: e.target.value })
                }
              />
            </Field>

            <Field label="Price">
              <input
                type="number"
                min="0"
                step="0.01"
                className="form-control form-control-sm"
                style={styles.input}
                value={detail.price}
                onChange={(e) =>
                  setDetail({ ...detail, price: e.target.value })
                }
              />
            </Field>

            <Field label="Discount %">
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                className="form-control form-control-sm"
                style={styles.input}
                value={detail.discountPercent}
                onChange={(e) =>
                  setDetail({ ...detail, discountPercent: e.target.value })
                }
              />
            </Field>

            <Field label="Customer" col="col-md-2">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={detail.customerId}
                onChange={(e) =>
                  setDetail({ ...detail, customerId: e.target.value })
                }
              >
                <option value="">All Customers</option>

                {customers.map((c, index) => {
                  const id = val(c, "Id", "id") || index;
                  return (
                    <option key={id} value={id}>
                      {safe(
                        val(c, "CustomerName", "customerName", "Name", "name"),
                      )}
                    </option>
                  );
                })}
              </select>
            </Field>

            <Field label="Remarks" col="col-md-2">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={detail.remarks}
                onChange={(e) =>
                  setDetail({ ...detail, remarks: e.target.value })
                }
              />
            </Field>

            <div className="col-md-1 d-flex align-items-end">
              <button
                type="button"
                className="btn btn-primary btn-sm w-100"
                style={styles.btn}
                onClick={addDetail}
              >
                Add
              </button>
            </div>
          </div>

          <TableBox className="mb-2">
            <table
              className="table table-bordered table-hover align-middle"
              style={{ fontSize: 11, minWidth: 1300, marginBottom: 0 }}
            >
              <thead>
                <tr>
                  {[
                    "Item",
                    "Min Qty",
                    "Max Qty",
                    "Price",
                    "Discount %",
                    "Customer",
                    "Remarks",
                    "Action",
                  ].map((h) => (
                    <th key={h} style={styles.th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {details.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="text-center text-muted"
                      style={{ padding: 18 }}
                    >
                      No pricing rules added
                    </td>
                  </tr>
                ) : (
                  details.map((d, i) => (
                    <tr key={i}>
                      <td style={styles.td}>
                        {safe(d.itemCode)} - {safe(d.itemName)}
                      </td>
                      <td style={styles.td}>{money(d.minQty)}</td>
                      <td style={styles.td}>
                        {d.maxQty ? money(d.maxQty) : "-"}
                      </td>
                      <td style={styles.td}>₹ {money(d.price)}</td>
                      <td style={styles.td}>{money(d.discountPercent)}</td>
                      <td style={styles.td}>
                        {d.customerId
                          ? getCustomerName(d.customerId) || "-"
                          : "ALL"}
                      </td>
                      <td style={styles.td}>{safe(d.remarks) || "-"}</td>
                      <td style={styles.td}>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          style={styles.btn}
                          onClick={() => removeDetail(i)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </TableBox>

          <div className="d-flex justify-content-end gap-2 mb-3">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              style={styles.btn}
              onClick={() => window.print()}
            >
              Print
            </button>

            <button
              type="button"
              className="btn btn-success btn-sm"
              style={styles.btn}
              disabled={saving || loading}
              onClick={save}
            >
              {saving ? "Saving..." : "Save Price List"}
            </button>
          </div>

          <Section title="Price List Register" />

          <TableBox>
            <table
              className="table table-bordered table-hover align-middle"
              style={{ fontSize: 11, minWidth: 1200, marginBottom: 0 }}
            >
              <thead>
                <tr>
                  {[
                    "Code",
                    "Name",
                    "Category",
                    "From",
                    "To",
                    "Default",
                    "Status",
                    "Rules",
                  ].map((h) => (
                    <th key={h} style={styles.th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="text-center text-muted"
                      style={{ padding: 18 }}
                    >
                      {loading ? "Loading..." : "No price lists found"}
                    </td>
                  </tr>
                ) : (
                  rows.map((r, index) => (
                    <tr key={val(r, "Id", "id") || index}>
                      <td style={styles.td}>
                        {safe(val(r, "PriceListCode", "priceListCode"))}
                      </td>
                      <td style={styles.td}>
                        {safe(val(r, "PriceListName", "priceListName"))}
                      </td>
                      <td style={styles.td}>
                        {safe(val(r, "PriceCategory", "priceCategory"))}
                      </td>
                      <td style={styles.td}>
                        {safe(val(r, "EffectiveFrom", "effectiveFrom")).slice(
                          0,
                          10,
                        )}
                      </td>
                      <td style={styles.td}>
                        {safe(val(r, "EffectiveTo", "effectiveTo")).slice(
                          0,
                          10,
                        ) || "-"}
                      </td>
                      <td style={styles.td}>
                        {val(r, "IsDefault", "isDefault") ? "YES" : "NO"}
                      </td>
                      <td style={styles.td}>
                        {val(r, "IsActive", "isActive") ? "ACTIVE" : "INACTIVE"}
                      </td>
                      <td style={styles.td}>
                        {safe(
                          val(r, "TotalItems", "totalItems", "Rules", "rules"),
                        ) || "0"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </TableBox>
        </div>
      </div>
    </div>
  );
}

function Section({ title }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 800,
        color: "#0f766e",
        borderBottom: "1px solid #e9ecef",
        paddingBottom: 4,
        marginBottom: 8,
      }}
    >
      {title}
    </div>
  );
}

function TableBox({ children }) {
  return (
    <div
      style={{
        maxHeight: 350,
        overflow: "auto",
        border: "1px solid #e9ecef",
        borderRadius: 10,
      }}
      className="mb-2"
    >
      {children}
    </div>
  );
}

function Field({ label, children, col = "col-md-2" }) {
  return (
    <div className={col}>
      <label
        style={{
          fontSize: 10,
          fontWeight: 700,
          marginBottom: 3,
          color: "#495057",
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function Stat({ title, value }) {
  return (
    <div className="col-md-3">
      <div
        style={{
          border: "1px solid #e9ecef",
          background: "#f8fafc",
          borderRadius: 10,
          padding: 9,
          borderTop: "3px solid #0f766e",
        }}
      >
        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 800 }}>
          {title}
        </div>

        <div style={{ fontSize: 15, color: "#0f766e", fontWeight: 900 }}>
          {safe(value)}
        </div>
      </div>
    </div>
  );
}
